import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'wouter';
import { useAuth, useLogout } from '../lib/auth';
import { useDrafts } from '../lib/drafts';
import type { Project } from '../lib/projects';
import { api } from '../lib/api';
import { useSetupOutputs } from '../lib/setup';
import { getSetupReadiness } from '../lib/setupState';

interface NavItem {
  key: string;
  step?: string;
  label: string;
  icon: string;
  href: string;
  match: string;
  badge?: { text: string; tone: 'cyan' | 'amber' | 'emerald' };
  done?: boolean;
}

interface Props {
  projectId?: string;
  /** Mobile drawer open state — controlled by AppShell. Ignored at md+. */
  mobileOpen?: boolean;
  /** Mobile drawer close callback — fired when a nav link is clicked
   *  or the backdrop is tapped. */
  onMobileClose?: () => void;
}

export function SideNav({ projectId, mobileOpen = false, onMobileClose }: Props) {
  const { user } = useAuth();
  const logout = useLogout();
  const [location] = useLocation();
  const inAdmin = location.startsWith('/admin');

  const drafts = useDrafts(projectId, 'pending');
  const setup = useSetupOutputs(projectId);
  const project = useQuery({
    enabled: !!projectId,
    queryKey: ['projects', projectId],
    queryFn: () => api<{ project: Project }>(`/api/projects/${projectId}`).then((r) => r.project),
  });
  const pendingCount = drafts.data?.length ?? 0;
  const setupReady = getSetupReadiness(setup.data).ready;

  const workspace: NavItem[] = projectId
    ? [
        { key: 'home', label: 'Home', icon: 'space_dashboard', href: `/projects/${projectId}`, match: '/projects/:id' },
        { key: 'setup', step: '01', label: 'Strategy', icon: 'tune', href: `/projects/${projectId}/setup`, match: '/projects/:id/setup', done: setupReady },
        { key: 'generator', step: '02', label: 'Content', icon: 'account_tree', href: `/projects/${projectId}/generator`, match: '/projects/:id/generator' },
        { key: 'posts', step: '03', label: 'Posts', icon: 'auto_stories', href: `/projects/${projectId}/posts`, match: '/projects/:id/posts', badge: pendingCount > 0 ? { text: String(pendingCount), tone: 'amber' } : undefined },
      ]
    : [{ key: 'projects', label: 'Brands', icon: 'apps', href: '/', match: '/' }];

  const manage: NavItem[] = projectId
    ? [
        { key: 'team', label: 'Team', icon: 'groups', href: `/projects/${projectId}/team`, match: '/projects/:id/team' },
        { key: 'settings', label: 'Settings', icon: 'settings', href: `/projects/${projectId}/settings`, match: '/projects/:id/settings' },
      ]
    : [];

  if (!projectId && user?.isAdmin) {
    manage.push({ key: 'admin', label: 'Admin', icon: 'admin_panel_settings', href: '/admin', match: '/admin' });
  }

  // Close the mobile drawer when a nav link is tapped.
  const handleNavTap = () => {
    if (onMobileClose) onMobileClose();
  };

  return (
    <aside
      className={`bg-surface border-r border-border-color font-body flex flex-col
                  fixed inset-y-0 left-0 z-50 w-[280px] transform transition-transform duration-200 ease-out
                  md:relative md:translate-x-0 md:w-[240px] md:h-full md:shrink-0
                  ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
    >
      <div className="flex flex-col h-full justify-between p-4 overflow-y-auto scroll-thin">
        <div className="flex flex-col gap-6">
          {/* Brand mark — visible on desktop, hidden on mobile (top bar shows it instead) */}
          <Link href="/" onClick={handleNavTap} className="hidden md:flex items-center gap-2 hover:opacity-90" title="All brands">
            <img src="/bubbles-logo.png" alt="Bubbles" className="h-8 w-8 object-contain" />
            <span className="bubbles-gradient font-display text-2xl font-bold tracking-wider uppercase">Bubbles</span>
          </Link>

          {/* Mobile-only close button at the top of the drawer */}
          <div className="md:hidden flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted">Menu</span>
            <button
              type="button"
              onClick={handleNavTap}
              aria-label="Close menu"
              className="flex items-center justify-center w-9 h-9 rounded border border-border-color text-text-primary hover:bg-surface-2 transition-colors"
            >
              <span className="material-symbols-outlined text-[20px]">close</span>
            </button>
          </div>

          {projectId && (
            <div className="-mt-3 border-l-2 border-accent-cyan/70 pl-3">
              <Link
                href="/"
                onClick={handleNavTap}
                className="group flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted/70 hover:text-accent-cyan transition-colors"
              >
                <span className="material-symbols-outlined text-[12px] transition-transform group-hover:-translate-x-0.5">arrow_back</span>
                <span>All brands</span>
              </Link>
              <p className="mt-1 truncate text-sm font-bold text-text-primary" title={project.data?.name ?? 'Brand'}>
                {project.data?.name ?? 'Brand'}
              </p>
            </div>
          )}

          {inAdmin && (
            <div className="-mt-3 rounded border border-accent-amber/40 bg-accent-amber/10 px-2 py-1.5 flex items-center gap-2">
              <span className="material-symbols-outlined text-accent-amber text-[16px]">shield_person</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-accent-amber font-semibold">Admin Mode</span>
            </div>
          )}

          <div>
            <SectionLabel>{projectId ? 'Workspace' : 'Navigate'}</SectionLabel>
            <nav className="flex flex-col gap-1 mt-1">
              {workspace.map((item) => <NavLink key={item.key} item={item} location={location} onTap={handleNavTap} />)}
            </nav>
          </div>

          {manage.length > 0 && (
            <div>
              <SectionLabel>{projectId ? 'Manage' : 'Admin'}</SectionLabel>
              <nav className="flex flex-col gap-1 mt-1">
                {manage.map((item) => <NavLink key={item.key} item={item} location={location} onTap={handleNavTap} />)}
              </nav>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {user && (
            <div className="flex items-center justify-between gap-2 text-[10px] font-mono text-muted">
              <span className="truncate flex-1 min-w-0" title={user.email}>{user.email}</span>
              <div className="flex items-center gap-2 shrink-0">
                {projectId && user.isAdmin && !inAdmin && (
                  <Link href="/admin" onClick={handleNavTap} className="material-symbols-outlined text-muted hover:text-accent-amber text-[16px] transition-colors" title="Switch to admin mode">shield_person</Link>
                )}
                <button onClick={() => logout.mutate()} className="text-muted hover:text-accent-red transition-colors uppercase tracking-wider">logout</button>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 p-2 rounded bg-background-dark border border-border-color">
            <div className="w-2 h-2 rounded-full bg-accent-emerald animate-pulse" />
            <span className="font-mono text-[10px] text-muted uppercase tracking-wider">Team online</span>
          </div>
        </div>
      </div>
    </aside>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted/70 px-1">{children}</p>;
}

function NavLink({ item, location, onTap }: { item: NavItem; location: string; onTap?: () => void }) {
  const active = item.key === 'home'
    ? location === item.href
    : location === item.href || location.startsWith(`${item.href}/`);
  return (
    <Link
      href={item.href}
      onClick={onTap}
      className={`relative flex items-center gap-3 px-3 py-2.5 border-l-[3px] transition-colors ${
        active ? 'bg-surface-2 border-accent-cyan text-text-primary' : 'border-transparent text-muted hover:bg-surface-2 hover:text-text-primary'
      }`}
    >
      <span className="font-mono text-sm font-bold text-muted w-7 shrink-0 tabular-nums">{item.step ?? ''}</span>
      <span className="material-symbols-outlined text-[18px] shrink-0" style={{ fontVariationSettings: active ? "'FILL' 1" : "'FILL' 0" }}>{item.icon}</span>
      <span className="text-sm font-semibold flex-1">{item.label}</span>
      {item.done && <span className="material-symbols-outlined text-accent-emerald text-[16px]" title="Complete">check_circle</span>}
      {item.badge && <span className={`status-pill ${item.badge.tone === 'amber' ? 'status-pill-amber' : item.badge.tone === 'emerald' ? 'status-pill-emerald' : 'status-pill-cyan'} !px-1.5 !py-0`}>{item.badge.text}</span>}
    </Link>
  );
}
